/**
 * Mini-évaluateur d'expressions pour les déclencheurs conditionnels d'événements (`trigger.condition`).
 * Pas de `eval` : analyse descendante sur une grammaire fermée.
 *
 *   expr   := or ;  or := and ("||" and)* ;  and := not ("&&" not)* ;  not := "!" not | cmp
 *   cmp    := sum (("<" | "<=" | ">" | ">=" | "==" | "!=") sum)?
 *   sum    := prod (("+" | "-") prod)* ;  prod := unary (("*" | "/") unary)*
 *   unary  := "-" unary | atom ;  atom := number | identifier | "(" expr ")"
 *
 * Les identifiants (`debt_pct`, `service_index.sante`, `measure_active.ret60`…) sont résolus dans un dictionnaire
 * fourni par le moteur : tout identifiant inconnu est une erreur de contenu, signalée explicitement.
 * Les booléens sont représentés par 1 / 0.
 */

export type Scope = Readonly<Record<string, number>>;

type Tok = { t: "num"; v: number } | { t: "id"; v: string } | { t: "op"; v: string } | { t: "end" };

const OPS = ["<=", ">=", "==", "!=", "&&", "||", "<", ">", "+", "-", "*", "/", "!", "(", ")"];

export class ExprError extends Error {
  override name = "ExprError";
}

function tokenize(src: string): Tok[] {
  const out: Tok[] = [];
  let i = 0;
  while (i < src.length) {
    const c = src[i] ?? "";
    if (/\s/.test(c)) {
      i++;
      continue;
    }
    if (/[0-9.]/.test(c)) {
      const m = /^[0-9]*\.?[0-9]+(?:e-?[0-9]+)?/i.exec(src.slice(i));
      if (!m) throw new ExprError(`nombre invalide à la position ${i} dans « ${src} »`);
      out.push({ t: "num", v: Number(m[0]) });
      i += m[0].length;
      continue;
    }
    if (/[a-z_]/i.test(c)) {
      const m = /^[a-z_][a-z0-9_]*(?:\.[a-z_][a-z0-9_]*)*/i.exec(src.slice(i));
      if (!m) throw new ExprError(`identifiant invalide à la position ${i}`);
      out.push({ t: "id", v: m[0] });
      i += m[0].length;
      continue;
    }
    const op = OPS.find((o) => src.startsWith(o, i));
    if (!op) throw new ExprError(`caractère inattendu « ${c} » à la position ${i} dans « ${src} »`);
    out.push({ t: "op", v: op });
    i += op.length;
  }
  out.push({ t: "end" });
  return out;
}

/** Évalue `src` dans `scope`. Renvoie un nombre (comparaisons et opérateurs logiques → 1 ou 0). */
export function evaluate(src: string, scope: Scope): number {
  const toks = tokenize(src);
  let pos = 0;
  const peek = (): Tok => toks[pos] ?? { t: "end" };
  const isOp = (v: string): boolean => {
    const t = peek();
    return t.t === "op" && t.v === v;
  };
  const eat = (v: string): boolean => {
    if (isOp(v)) {
      pos++;
      return true;
    }
    return false;
  };
  const truthy = (x: number): number => (x !== 0 ? 1 : 0);

  const atom = (): number => {
    const t = peek();
    if (t.t === "num") {
      pos++;
      return t.v;
    }
    if (t.t === "id") {
      pos++;
      const v = scope[t.v];
      if (v === undefined) throw new ExprError(`variable inconnue « ${t.v} » dans « ${src} » (variables : ${Object.keys(scope).join(", ")})`);
      return v;
    }
    if (eat("(")) {
      const v = or();
      if (!eat(")")) throw new ExprError(`parenthèse fermante attendue dans « ${src} »`);
      return v;
    }
    throw new ExprError(`expression invalide dans « ${src} »`);
  };
  const unary = (): number => (eat("-") ? -unary() : atom());
  const prod = (): number => {
    let v = unary();
    for (;;) {
      if (eat("*")) v *= unary();
      else if (eat("/")) v /= unary();
      else return v;
    }
  };
  const sum = (): number => {
    let v = prod();
    for (;;) {
      if (eat("+")) v += prod();
      else if (eat("-")) v -= prod();
      else return v;
    }
  };
  const cmp = (): number => {
    const l = sum();
    if (eat("<")) return l < sum() ? 1 : 0;
    if (eat("<=")) return l <= sum() ? 1 : 0;
    if (eat(">")) return l > sum() ? 1 : 0;
    if (eat(">=")) return l >= sum() ? 1 : 0;
    if (eat("==")) return l === sum() ? 1 : 0;
    if (eat("!=")) return l !== sum() ? 1 : 0;
    return l;
  };
  const not = (): number => (eat("!") ? (not() !== 0 ? 0 : 1) : cmp());
  // Les opérateurs logiques renvoient 1/0 ; une expression purement arithmétique garde sa valeur.
  const and = (): number => {
    let v = not();
    while (eat("&&")) {
      const r = not();
      v = truthy(v) && truthy(r) ? 1 : 0;
    }
    return v;
  };
  const or = (): number => {
    let v = and();
    while (eat("||")) {
      const r = and();
      v = truthy(v) || truthy(r) ? 1 : 0;
    }
    return v;
  };

  const result = or();
  if (peek().t !== "end") throw new ExprError(`fin d'expression attendue dans « ${src} »`);
  return result;
}

/** Vrai si la condition est satisfaite. */
export function test(src: string, scope: Scope): boolean {
  return evaluate(src, scope) !== 0;
}
